import {
    AlertTriangle,
    CheckCircle2,
    ChevronRight,
    CircleHelp,
    FileText,
    ListTodo,
    MessageSquare,
} from "lucide-react";
import { HealthLang } from "../healthTypes";
import {
    healthNursingCoordinationSnippetMetaLabel,
    healthNursingCoordinationSnippetMetaValue,
    healthNursingCoordinationStepBadges,
    healthNursingCoordinationStepSurfaces,
} from "../healthVisualStyles";

const UI_LABELS = {
    handoffHeader: {
        En: "Signed handoff",
        Zh: "已簽核交接",
        Jp: "署名済み引き継ぎ",
    },
    viewHandoff: {
        En: "View full handoff",
        Zh: "查看完整交接",
        Jp: "引き継ぎ全文",
    },
    queueHeader: {
        En: "My nursing queue",
        Zh: "我的護理待辦",
        Jp: "看護キュー",
    },
    tasksCount: {
        En: "4 tasks",
        Zh: "4 項任務",
        Jp: "4件",
    },
    viewTasks: {
        En: "View all tasks",
        Zh: "查看全部任務",
        Jp: "すべて表示",
    },
    logHeader: {
        En: "Coordination log",
        Zh: "協調紀錄",
        Jp: "連携ログ",
    },
    logLatest: {
        En: "Latest",
        Zh: "最新",
        Jp: "最新",
    },
    viewLog: {
        En: "View full log",
        Zh: "查看完整紀錄",
        Jp: "ログ全文",
    },
    priorityHigh: { En: "High", Zh: "高", Jp: "高" },
    priorityMedium: { En: "Medium", Zh: "中", Jp: "中" },
    priorityLow: { En: "Low", Zh: "低", Jp: "低" },
    educationDone: {
        En: "Education completed",
        Zh: "衛教完成",
        Jp: "教育完了",
    },
    patientQuestion: {
        En: "Patient question",
        Zh: "病人提問",
        Jp: "患者からの質問",
    },
    escalation: {
        En: "Escalation",
        Zh: "升級通報",
        Jp: "エスカレーション",
    },
    signedReleased: {
        En: "Signed · Released",
        Zh: "已簽核 · 已發布",
        Jp: "署名済 · 公開",
    },
    labelPatient: { En: "Patient", Zh: "病人", Jp: "患者" },
    labelMrn: { En: "MRN", Zh: "病歷號", Jp: "MRN" },
    labelUnit: { En: "Unit", Zh: "病房", Jp: "病棟" },
    labelAttending: { En: "Attending", Zh: "主治醫師", Jp: "主治医" },
    signedPrefix: { En: "Signed", Zh: "簽核", Jp: "署名" },
    signedTime: {
        En: "Today, 7:42 AM",
        Zh: "今日 7:42",
        Jp: "本日 7:42",
    },
    planSummary: {
        En: "Plan summary",
        Zh: "計畫摘要",
        Jp: "計画概要",
    },
    planDetail: {
        En: "Continue antibiotics · ambulate TID · discharge teaching if afebrile 24h",
        Zh: "續用抗生素 · 每日三次下床 · 若 24 小時無發燒則進行出院衛教",
        Jp: "抗生剤継続 · 1日3回歩行 · 24時間無熱なら退院指導",
    },
    sortedByPriority: {
        En: "Sorted by priority.",
        Zh: "依優先順序排序。",
        Jp: "優先度順に並べ替え。",
    },
} as const;

function t(key: keyof typeof UI_LABELS, lang: HealthLang): string {
    return UI_LABELS[key][lang] ?? UI_LABELS[key].En;
}

const snippetShell =
    "relative w-full overflow-hidden rounded-lg border border-grey-100/90 bg-white text-left shadow-[0_4px_14px_rgba(16,24,40,0.06)] [text-align:left]";
const snippetInner = "px-3.5 pb-2.5 pt-3 text-left [text-align:left]";
const snippetCrop =
    "pointer-events-none select-none after:pointer-events-none after:absolute after:inset-x-0 after:bottom-0 after:z-[1] after:h-5 after:bg-gradient-to-t after:from-white after:to-transparent";
const metaLabel = healthNursingCoordinationSnippetMetaLabel;
const metaValue = healthNursingCoordinationSnippetMetaValue;
const rowDivider = "border-t border-grey-100/90";
const snippetLink =
    "mt-2 flex items-center justify-start gap-0.5 text-left text-[10px] font-medium [text-align:left]";

interface PreviewProps {
    lang: HealthLang;
}

export function HandoffPreviewSnippet({ lang }: PreviewProps) {
    return (
        <div className={`${snippetShell} ${snippetCrop} max-h-[12.5rem]`}>
            <div className={snippetInner}>
                <div className="flex items-center justify-start gap-1.5 border-b border-grey-100/90 pb-2 text-left">
                    <FileText
                        className="h-3.5 w-3.5 shrink-0 text-teal-600"
                        strokeWidth={1.75}
                        aria-hidden="true"
                    />
                    <span className="text-left text-[11px] font-semibold text-grey-800">
                        {t("handoffHeader", lang)}
                    </span>
                    <span className="ml-auto rounded-full bg-teal-50 px-1.5 py-0.5 text-center text-[9px] font-semibold text-teal-700">
                        {t("signedReleased", lang)}
                    </span>
                </div>
                <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1.5 text-left">
                    <div className="text-left">
                        <dt className={metaLabel}>{t("labelPatient", lang)}</dt>
                        <dd className={metaValue}>Maria Lopez</dd>
                    </div>
                    <div className="text-left">
                        <dt className={metaLabel}>{t("labelMrn", lang)}</dt>
                        <dd className={metaValue}>482910</dd>
                    </div>
                    <div className="text-left">
                        <dt className={metaLabel}>{t("labelUnit", lang)}</dt>
                        <dd className={metaValue}>Med-Surg 5A</dd>
                    </div>
                    <div className="text-left">
                        <dt className={metaLabel}>{t("labelAttending", lang)}</dt>
                        <dd className={metaValue}>A. Patel, MD</dd>
                    </div>
                </dl>
                <p className="mt-2 text-left text-[10px] leading-relaxed text-grey-500">
                    <span className="font-medium text-grey-600">
                        {t("signedPrefix", lang)}{" "}
                    </span>
                    {t("signedTime", lang)}
                </p>
                <p className={`${metaLabel} mt-2 normal-case tracking-normal`}>
                    {t("planSummary", lang)}
                </p>
                <p className="mt-0.5 line-clamp-2 text-left text-[10px] leading-relaxed text-grey-600">
                    {t("planDetail", lang)}
                </p>
                <p className={`${snippetLink} text-teal-600`}>
                    {t("viewHandoff", lang)}
                    <ChevronRight className="h-3 w-3" aria-hidden="true" />
                </p>
            </div>
        </div>
    );
}

const QUEUE_ROWS: Record<
    HealthLang,
    { id: string; task: string; patient: string; priority: "High" | "Medium" | "Low" }[]
> = {
    En: [
        { id: "education", task: "Patient education", patient: "Maria Lopez · 5A", priority: "High" },
        { id: "mobility", task: "Mobility follow-up", patient: "James Wu · 5A", priority: "Medium" },
        { id: "discharge", task: "Discharge teaching", patient: "Maria Lopez · 5A", priority: "Medium" },
        { id: "coordination", task: "Care coordination", patient: "Unit queue", priority: "Low" },
    ],
    Zh: [
        { id: "education", task: "病人衛教", patient: "Maria Lopez · 5A", priority: "High" },
        { id: "mobility", task: "活動追蹤", patient: "James Wu · 5A", priority: "Medium" },
        { id: "discharge", task: "出院衛教", patient: "Maria Lopez · 5A", priority: "Medium" },
        { id: "coordination", task: "照護協調", patient: "病房佇列", priority: "Low" },
    ],
    Jp: [
        { id: "education", task: "患者教育", patient: "Maria Lopez · 5A", priority: "High" },
        { id: "mobility", task: "離床フォロー", patient: "James Wu · 5A", priority: "Medium" },
        { id: "discharge", task: "退院指導", patient: "Maria Lopez · 5A", priority: "Medium" },
        { id: "coordination", task: "ケア連携", patient: "病棟キュー", priority: "Low" },
    ],
};

function priorityClass(priority: "High" | "Medium" | "Low") {
    if (priority === "High") return "bg-rose-50 text-rose-700";
    if (priority === "Medium") return "bg-amber-50 text-amber-700";
    return "bg-emerald-50 text-emerald-700";
}

function priorityLabel(priority: "High" | "Medium" | "Low", lang: HealthLang) {
    if (priority === "High") return t("priorityHigh", lang);
    if (priority === "Medium") return t("priorityMedium", lang);
    return t("priorityLow", lang);
}

export function QueuePreviewSnippet({ lang }: PreviewProps) {
    return (
        <div className={`${snippetShell} ${snippetCrop} max-h-[12.5rem]`}>
            <div className={snippetInner}>
                <div className="flex items-center justify-start gap-1.5 border-b border-grey-100/90 pb-2 text-left">
                    <ListTodo
                        className="h-3.5 w-3.5 shrink-0 text-sky-600"
                        strokeWidth={1.75}
                        aria-hidden="true"
                    />
                    <span className="text-left text-[11px] font-semibold text-grey-800">
                        {t("queueHeader", lang)}
                    </span>
                    <span className="ml-auto rounded-full bg-sky-50 px-1.5 py-0.5 text-center text-[9px] font-semibold text-sky-700">
                        {t("tasksCount", lang)}
                    </span>
                </div>
                <p className="mt-1.5 text-left text-[10px] text-grey-500">
                    {t("sortedByPriority", lang)}
                </p>
                <ul className="mt-1 divide-y divide-grey-100/90 text-left">
                    {(QUEUE_ROWS[lang] ?? QUEUE_ROWS.En).map((row) => (
                        <li
                            key={row.id}
                            className="flex items-start justify-between gap-2 py-1.5 text-left"
                        >
                            <div className="min-w-0 text-left">
                                <p className="truncate text-left text-[10px] font-medium text-grey-800">
                                    {row.task}
                                </p>
                                <p className="truncate text-left text-[10px] text-grey-500">
                                    {row.patient}
                                </p>
                            </div>
                            <span
                                className={`shrink-0 rounded px-1 py-0.5 text-center text-[10px] font-semibold uppercase tracking-wide ${priorityClass(row.priority)}`}
                            >
                                {priorityLabel(row.priority, lang)}
                            </span>
                        </li>
                    ))}
                </ul>
                <p className={`${snippetLink} ${rowDivider} pt-2 text-sky-600`}>
                    {t("viewTasks", lang)}
                    <ChevronRight className="h-3 w-3" aria-hidden="true" />
                </p>
            </div>
        </div>
    );
}

const LOG_ENTRIES: Record<
    HealthLang,
    {
        id: string;
        icon: typeof CheckCircle2;
        iconClass: string;
        titleKey: keyof typeof UI_LABELS;
        meta: string;
        note: string;
    }[]
> = {
    En: [
        {
            id: "education",
            icon: CheckCircle2,
            iconClass: "text-emerald-500",
            titleKey: "educationDone",
            meta: "RN Chen · 9:14 AM",
            note: "Antibiotic teaching — patient verbalized understanding",
        },
        {
            id: "question",
            icon: CircleHelp,
            iconClass: "text-sky-500",
            titleKey: "patientQuestion",
            meta: "Bedside · 10:02 AM",
            note: "Asked about mobility restrictions after procedure",
        },
        {
            id: "escalation",
            icon: AlertTriangle,
            iconClass: "text-amber-500",
            titleKey: "escalation",
            meta: "Charge RN · 10:18 AM",
            note: "Pain score 7/10 — notified attending team",
        },
    ],
    Zh: [
        {
            id: "education",
            icon: CheckCircle2,
            iconClass: "text-emerald-500",
            titleKey: "educationDone",
            meta: "陳護理師 · 9:14",
            note: "抗生素衛教 — 病人已複述理解",
        },
        {
            id: "question",
            icon: CircleHelp,
            iconClass: "text-sky-500",
            titleKey: "patientQuestion",
            meta: "床邊 · 10:02",
            note: "詢問術後活動限制",
        },
        {
            id: "escalation",
            icon: AlertTriangle,
            iconClass: "text-amber-500",
            titleKey: "escalation",
            meta: "值班護理長 · 10:18",
            note: "疼痛分數 7/10 — 已通知醫師團隊",
        },
    ],
    Jp: [
        {
            id: "education",
            icon: CheckCircle2,
            iconClass: "text-emerald-500",
            titleKey: "educationDone",
            meta: "陳 RN · 9:14",
            note: "抗生剤指導 — 患者が理解を復唱",
        },
        {
            id: "question",
            icon: CircleHelp,
            iconClass: "text-sky-500",
            titleKey: "patientQuestion",
            meta: "ベッドサイド · 10:02",
            note: "術後の活動制限について質問",
        },
        {
            id: "escalation",
            icon: AlertTriangle,
            iconClass: "text-amber-500",
            titleKey: "escalation",
            meta: "チャージ RN · 10:18",
            note: "疼痛スコア 7/10 — 主治医チームに通知",
        },
    ],
};

export function LogPreviewSnippet({ lang }: PreviewProps) {
    return (
        <div className={`${snippetShell} ${snippetCrop} max-h-[12.5rem]`}>
            <div className={snippetInner}>
                <div className="flex items-center justify-start gap-1.5 border-b border-grey-100/90 pb-2 text-left">
                    <MessageSquare
                        className="h-3.5 w-3.5 shrink-0 text-violet-600"
                        strokeWidth={1.75}
                        aria-hidden="true"
                    />
                    <span className="text-left text-[11px] font-semibold text-grey-800">
                        {t("logHeader", lang)}
                    </span>
                    <span className="ml-auto flex items-center justify-end gap-0.5 text-left text-[10px] text-grey-500">
                        {t("logLatest", lang)}
                        <ChevronRight className="h-2.5 w-2.5 rotate-90" aria-hidden="true" />
                    </span>
                </div>
                <ul className="mt-1.5 space-y-1.5 text-left">
                    {(LOG_ENTRIES[lang] ?? LOG_ENTRIES.En).map((entry) => {
                        const Icon = entry.icon;
                        return (
                            <li key={entry.id} className="flex items-start gap-2 text-left">
                                <Icon
                                    className={`mt-0.5 h-3 w-3 shrink-0 ${entry.iconClass}`}
                                    strokeWidth={1.75}
                                    aria-hidden="true"
                                />
                                <div className="min-w-0 text-left">
                                    <div className="flex flex-wrap items-baseline justify-start gap-x-1.5 text-left">
                                        <span className="text-left text-[10px] font-medium text-grey-800">
                                            {t(entry.titleKey, lang)}
                                        </span>
                                        <span className="text-left text-[10px] text-grey-500">
                                            {entry.meta}
                                        </span>
                                    </div>
                                    <p className="line-clamp-1 text-left text-[10px] leading-relaxed text-grey-600">
                                        {entry.note}
                                    </p>
                                </div>
                            </li>
                        );
                    })}
                </ul>
                <p className={`${snippetLink} ${rowDivider} pt-2 text-violet-600`}>
                    {t("viewLog", lang)}
                    <ChevronRight className="h-3 w-3" aria-hidden="true" />
                </p>
            </div>
        </div>
    );
}

export const COORDINATION_STEP_SURFACES = healthNursingCoordinationStepSurfaces;

export const COORDINATION_STEP_BADGES = healthNursingCoordinationStepBadges;

export const COORDINATION_PREVIEW_COMPONENTS = [
    HandoffPreviewSnippet,
    QueuePreviewSnippet,
    LogPreviewSnippet,
] as const;
